import React from 'react';
import { Button, Divider, Tooltip } from 'antd';
import {
	AudioOutlined,
	DeleteOutlined,
	PauseCircleOutlined,
	PauseOutlined,
} from '@ant-design/icons';
import OWAudioWaveform from './OWAudioWaveform';
import { UseAudioRecorderReturn } from '../../hooks/useAudioRecorder';

interface AudioRecorderPanelProps {
	recorder: UseAudioRecorderReturn;
	onSend: (blob: Blob, file: File) => void;
	onDelete: () => void;
	uploading?: boolean;
	sendLabel?: string;
}

const formatTime = (ms: number): string => {
	const totalSeconds = Math.floor(ms / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	const paddedSeconds = seconds < 10 ? '0' + seconds : '' + seconds;
	return minutes + ':' + paddedSeconds;
};

const AudioRecorderPanel: React.FC<AudioRecorderPanelProps> = ({
	recorder,
	onSend,
	onDelete,
	uploading,
	sendLabel = 'Send',
}) => {
	const handleSend = () => {
		recorder.finalizeRecording().then((blob) => {
			if (blob) {
				const file = new File([blob], 'voice-note.webm', {
					type: blob.type || 'audio/webm',
				});
				onSend(blob, file);
			}
		});
	};

	const isActive = recorder.isRecording || recorder.hasRecording;

	return (
		<div
			className="flex flex-col gap-3 border border-solid border-grey-5 px-4 py-3"
			style={{ minWidth: 280 }}
		>
			{/* Row 1: Play/Pause + Waveform + Timer */}
			<div className="flex items-center gap-2">
				{recorder.isPaused && (
					<button
						onClick={recorder.isPlaying ? recorder.stopPreview : recorder.playPreview}
						className="flex h-8 w-8 flex-shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 outline-none"
						type="button"
						disabled={!!uploading}
					>
						{recorder.isPlaying ? (
							<PauseCircleOutlined style={{ fontSize: 20 }} />
						) : (
							<i style={{ fontSize: 20 }} className="icons8-font icons8-play-2" />
						)}
					</button>
				)}
				<div className="relative h-10 flex-1">
					{isActive ? (
						<OWAudioWaveform
							stream={recorder.stream}
							isPaused={recorder.isPaused}
							playbackProgress={recorder.isPlaying ? recorder.playbackProgress : undefined}
						/>
					) : (
						<div className="h5regular flex h-full items-center text-character-primary">
							Ready to record
						</div>
					)}
				</div>
				<span className="h5regular min-w-[40px] text-right text-character-primary ">
					{formatTime(recorder.durationMs)}
				</span>
			</div>

			<Divider style={{ margin: 0 }} />

			{/* Row 2: Delete - Pause/Resume - Send */}
			<div className="flex items-center justify-between">
				<Tooltip title="Delete">
					<Button
						shape="circle"
						icon={<DeleteOutlined />}
						onClick={onDelete}
						disabled={!!uploading}
						className="bg-grey-4"
					/>
				</Tooltip>

				<Tooltip title={recorder.isPaused ? 'Resume' : 'Pause'}>
					<Button
						shape="circle"
						onClick={recorder.togglePause}
						disabled={!recorder.isRecording || !!uploading}
						icon={recorder.isPaused ? <AudioOutlined /> : <PauseOutlined />}
					/>
				</Tooltip>

				<Tooltip title={sendLabel}>
					<Button
						type="primary"
						loading={!!uploading}
						onClick={handleSend}
						disabled={!isActive}
						ghost
					>
						{sendLabel}
					</Button>
				</Tooltip>
			</div>
		</div>
	);
};

export default AudioRecorderPanel;
